import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { useAppDispatch } from '../../store';
import { verifyCheckoutSession, fetchCurrentSubscription } from '../../features/subscriptions/subscriptionsSlice';

const SubscriptionSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      dispatch(verifyCheckoutSession(sessionId))
        .unwrap()
        .then(() => {
          setVerified(true);
          setVerifying(false);
        })
        .catch(() => {
          dispatch(fetchCurrentSubscription())
            .unwrap()
            .then(() => setVerified(true))
            .catch(() => setVerified(false))
            .finally(() => setVerifying(false));
        });
    } else {
      dispatch(fetchCurrentSubscription())
        .unwrap()
        .then(() => setVerified(true))
        .catch(() => setVerified(false))
        .finally(() => setVerifying(false));
    }
  }, [dispatch, searchParams]);

  if (verifying) {
    return (
      <div className="animate-fade-in d-flex justify-content-center align-items-center" style={{ padding: '3rem 1.5rem', minHeight: 500 }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" style={{ color: 'var(--brand-primary)', width: 48, height: 48 }} />
          <p className="mt-3" style={{ color: 'var(--text-muted)' }}>
            {t('verifying_payment', 'Verifying your payment...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in d-flex justify-content-center align-items-center" style={{ padding: '3rem 1.5rem', minHeight: 500 }}>
      <div
        style={{
          maxWidth: 520,
          width: '100%',
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: 20,
          boxShadow: 'var(--shadow-lg)',
          textAlign: 'center',
          padding: '3rem 2rem',
        }}
      >
        <div
          style={{
            width: 80, height: 80, borderRadius: '50%',
            background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <FaCheckCircle size={40} style={{ color: '#22c55e' }} />
        </div>
        <h3 className="fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          {verified
            ? t('subscription_success_title', 'Subscription Activated!')
            : t('subscription_processing_title', 'Payment Received!')}
        </h3>
        <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
          {verified
            ? t('subscription_success_msg', 'Your subscription has been successfully activated. You can now enjoy all the features of your plan.')
            : t('subscription_processing_msg', 'Your payment has been received. Your subscription will be activated shortly.')}
        </p>
        <Button variant="primary" className="btn-modern btn-primary" onClick={() => navigate('/subscription')}>
          {t('view_subscription', 'View Subscription')}
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
